function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import React, { useState } from 'react';
import { clsx } from 'clsx';
import { useSegmentFilterController } from 'common/contexts/react/segment-filter-controller-context';
import { useTranslation } from 'common/i18n/hooks';
import { CheckboxReact } from 'common/components/checkbox';
import { ToggleTheme } from 'common/components/toggle/types';
import { ToggleReact } from 'common/components/toggle';
import { ColorPicker } from 'common/query-builder/chart-customization/colors/color-picker';
import { ColorThemeSwatch } from 'common/query-builder/chart-customization/colors/swatch';
import { getColorsForMode } from 'common/query-builder/chart-customization/colors/utils';
import { ButtonReact } from 'common/components/button';
import { useThemes } from 'common/contexts/react/themes-context';
import { DropMenuReact, PositionParent } from 'common/components/drop-menu';
import { Visibility } from 'common/components/base/stackable';
import { getSeriesMatchPath } from 'common/charts/contexts/utils';
import { useQueryContextManager } from 'common/contexts/react/query-context-manager';
import { flattenColumns, getNumSegments } from 'common/components/data-table/utils';
import styles from './index.module.styl';
type SegmentColorPickerProps = {
  segmentColor: string;
  path: string;
  numBoardReports?: number;
  positionParent: PositionParent;
  onClose: () => void;
};
export function SegmentColorPicker(_ref: SegmentColorPickerProps) {
  var _segmentFilterControl;
  let {
    segmentColor,
    path,
    numBoardReports,
    positionParent,
    onClose
  }: SegmentColorPickerProps = _ref;
  const themes = useThemes();
  const {
    t
  } = useTranslation();
  const queryContextManager = useQueryContextManager();
  const segmentFilterController = useSegmentFilterController();
  const [color, setColor] = useState(segmentColor);
  const [initialColor] = useState(segmentColor);
  const [initialLegendOverrides] = useState(_objectSpread({}, segmentFilterController.globalAndLocalOverrides));
  const [applyToBoard, setApplyToBoard] = useState(false);
  const [isDefault, setIsDefault] = useState(!segmentFilterController.localOverrides[path] && !segmentFilterController.localOverrides[getSeriesMatchPath(path)]);

  // hides the board coloring checkbox when the user is actively editing a bookmark inside the report editor
  // when the report editor is loaded, the bookmark params are loaded onto the `queryContextManager` context
  const isBoardColoringEnabled = queryContextManager.query.bookmarkType === null && segmentFilterController.isBoardsColoringEnabled();
  const tabs = [{
    label: t("chart-customization.theme"),
    value: "theme"
  }, {
    label: t("common.custom"),
    value: "custom"
  }];
  const defaultColor = segmentFilterController.getItemColor(0, path, {
    ignoreOverrides: true
  });
  const theme = segmentFilterController.getTheme();
  const themeColors = getColorsForMode(theme, (_segmentFilterControl = segmentFilterController.themesResource) === null || _segmentFilterControl === void 0 ? void 0 : _segmentFilterControl.isDarkMode());
  const activeThemeIndex = isDefault ? null : themeColors.findIndex(c => c === color);
  const [activeTab, setActiveTab] = useState(activeThemeIndex === -1 ? "custom" : "theme");

  /** board overrides use a partial path match */
  const setGlobalOverride = (segmentPath: string, overrideColor: string) => {
    themes.setLegendOverride(getSeriesMatchPath(segmentPath), overrideColor ? {
      color: overrideColor
    } : null);
  };
  const handleColorChange = function handleColorChange(updatedColor: string | null) {
    let isDefaultColor = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    setColor(updatedColor !== null && updatedColor !== void 0 ? updatedColor : defaultColor);
    setIsDefault(isDefaultColor);
    segmentFilterController.setLegendOverride(path, updatedColor ? {
      color: updatedColor
    } : null);
    if (applyToBoard) {
      setGlobalOverride(path, updatedColor);
    }
  };
  const handleApplyToBoardToggle = (on: boolean) => {
    setApplyToBoard(on);
    if (on) {
      setGlobalOverride(path, color);
    } else {
      themes.legendOverrides = {};
    }
  };
  const handleClose = () => {
    var _segmentFilterControl2, _segmentFilterControl3, _segmentFilterControl4, _segmentFilterControl5;
    /** persist color selection on dropdown close */
    if (initialColor !== color) {
      segmentFilterController.handleLegendChanges();
    } else {
      segmentFilterController.localOverrides = _objectSpread({}, initialLegendOverrides);
    }
    onClose();
    mixpanel.track("[Colors] Assign Legend Color", {
      path,
      color,
      type: activeTab,
      themeName: (_segmentFilterControl2 = segmentFilterController.getTheme()) === null || _segmentFilterControl2 === void 0 ? void 0 : _segmentFilterControl2.name,
      resultsLength: (_segmentFilterControl3 = segmentFilterController.sections[0]) === null || _segmentFilterControl3 === void 0 || (_segmentFilterControl3 = _segmentFilterControl3.items) === null || _segmentFilterControl3 === void 0 ? void 0 : _segmentFilterControl3.length,
      segmentCount: getNumSegments(flattenColumns((_segmentFilterControl4 = (_segmentFilterControl5 = segmentFilterController.sections[0]) === null || _segmentFilterControl5 === void 0 ? void 0 : _segmentFilterControl5.tableColumns) !== null && _segmentFilterControl4 !== void 0 ? _segmentFilterControl4 : [])) - 1
    });
  };
  React.useEffect(() => {
    segmentFilterController.notifySeriesHovered({
      seriesId: path
    });
    return () => segmentFilterController.notifySeriesHovered(null);
  }, []);
  return <DropMenuReact open experimentalPositioning positionParent={positionParent} onChange={ev => ev.detail.state === Visibility.Closed && handleClose()} data-sentry-element="DropMenuReact" data-sentry-component="SegmentColorPicker" data-sentry-source-file="index.tsx">
      <div className={styles.segmentColorPicker} elref='segmentColorPicker'>
        <div className={styles.tabBar}>
          <ToggleReact options={tabs} selected={activeTab} onSelect={ev => setActiveTab(ev.detail.selected)} theme={ToggleTheme.TabBarLite} data-sentry-element="ToggleReact" data-sentry-source-file="index.tsx" />
        </div>
        {activeTab === "theme" ? <div className={styles.line}>
            <ColorThemeSwatch theme={theme} hideName activeIndex={activeThemeIndex} onColorClick={index => handleColorChange(themeColors[index])} />
            <ButtonReact readonly={isDefault} pressed={isDefault} onClick={() => handleColorChange(null, true)}>
              {t("common.default")}
            </ButtonReact>
          </div> : <ColorPicker color={color} onChange={handleColorChange} />}

        {/* feature gate board level color assignment as we roll out */}
        {isBoardColoringEnabled && <CheckboxReact checked={applyToBoard} onClick={() => handleApplyToBoardToggle(!applyToBoard)} className={clsx(styles.checkbox, {
        [styles.afterTheme]: activeTab === "theme"
      })}>
            {t("chart-customization.set-color-for-all-checkbox", {
          n: numBoardReports || ""
        })}
          </CheckboxReact>}
      </div>
    </DropMenuReact>;
}